import AdDetailModel from "@/models/AdDetailModel";
import PillButton from "../PillButton";
import FlagIcon from "@mui/icons-material/Flag";
import { useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { CreateAdReportCase } from "@/case/actions/CreateAdReportCase";
import {
  AdReportReason,
  REPORT_REASONS,
} from "@/backend/case/models/AdReportCase";
import Image from "next/image";
import ClapIcon from "@/../public/icons/clap.png";
import CloseIcon from "@mui/icons-material/Close";
import { UpdateAdReportViewModel } from "@/case/models/UpdateAdReportViewModel";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { UpdateAdReportCase } from "@/case/actions/UpdateAdReportCase";
import ControlledTextField from "@/app/(browse)/sell/(ad)/ad/form/ControlledTextField";
import { useContactDetails } from "@/app/layout/useContactDetails";

function AdReportMenu({
  anchorEl,
  open,
  onClose,
  onSelect,
}: {
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
  onSelect: (reason: AdReportReason) => void;
}) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        horizontal: "center",
        vertical: "bottom",
      }}
      transformOrigin={{
        horizontal: "center",
        vertical: "top",
      }}
    >
      {REPORT_REASONS.map((reason) => (
        <MenuItem
          key={reason.code}
          onClick={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            onSelect(reason);
          }}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          {reason.title}
        </MenuItem>
      ))}
    </Menu>
  );
}

function AdReportDialog({
  ad,
  adReport,
  onClose,
}: {
  ad: AdDetailModel;
  adReport: UpdateAdReportViewModel;
  onClose: () => void;
}) {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { contactDetails } = useContactDetails();

  return (
    <Dialog open={true} onClose={onClose}>
      <Box
        sx={{
          maxWidth: "400px",
        }}
      >
        <Box
          sx={{
            backgroundColor: "grey.200",
            padding: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FlagIcon /> Report ad
            </Typography>
            <Typography variant="h6">{ad.title}</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              backgroundColor: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            padding: 2,
          }}
        >
          {!isSubmitted ? (
            <AdReportForm
              adReport={{
                ...adReport,
                email: contactDetails?.email || "",
              }}
              onComplete={() => setIsSubmitted(true)}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
              }}
            >
              <Image src={ClapIcon} alt="Thank you" width={48} height={48} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Typography>Thank you.</Typography>
                <Typography variant="body2">
                  Our team will review the information you provided and take
                  appropriate action.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

function AdReportForm({
  adReport,
  onComplete,
}: {
  adReport: UpdateAdReportViewModel;
  onComplete: () => void;
}) {
  const methods = useForm<UpdateAdReportViewModel>({
    defaultValues: adReport,
  });

  const onSubmit: SubmitHandler<UpdateAdReportViewModel> = async (data) => {
    await UpdateAdReportCase(data);
    onComplete();
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onSubmit)}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Image src={ClapIcon} alt="Thank you" width={48} height={48} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Typography>Thank you. We appreciate your help.</Typography>
            <Typography variant="body2">
              Please provide any additional information you think would be
              helpful.
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <ControlledTextField
            name="details"
            label="Tell us more about the issue"
            rows={4}
          />
          <ControlledTextField name="email" label="Email (optional)" />
        </Box>
        <PillButton
          sx={{
            alignSelf: "end",
          }}
          type="submit"
          primary
          loading={methods.formState.isSubmitting}
          disabled={methods.formState.isSubmitting}
        >
          Submit Report
        </PillButton>
      </Box>
    </FormProvider>
  );
}

export function AdReportButton({ ad }: { ad: AdDetailModel }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [adReport, setAdReport] = useState<null | UpdateAdReportViewModel>(
    null
  );
  const isMenuOpen = Boolean(menuAnchorEl);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  return (
    <>
      <PillButton
        startIcon={<FlagIcon />}
        variant="outlined"
        sx={{
          backgroundColor: "white",
        }}
        compact
        onClick={handleMenuOpen}
      >
        Report Ad
      </PillButton>
      <AdReportMenu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onSelect={async (reason) => {
          const updateViewModel = await CreateAdReportCase({
            key: ad.id,
            reason: reason.code,
          });
          setAdReport(updateViewModel);
          handleMenuClose();
        }}
      />
      {adReport && (
        <AdReportDialog
          ad={ad}
          adReport={adReport}
          onClose={() => setAdReport(null)}
        />
      )}
    </>
  );
}
